import { ChangeEvent, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Project, RuuviTagInput } from '../types'
import { useDispatch } from 'react-redux'
import { getProjects } from '../redux/slices/projects'
import { getCompanies } from '../redux/slices/companies'
import { getLiftsByProjectId } from '../redux/slices/lifts'
import { getLevelsByProjectId } from '../redux/slices/levels'
import { useFormik, FormikHelpers } from 'formik'
import { Button, Grid, Stack, TextField } from '@mui/material'
import LevelSelect from './LevelSelect'
import LiftSelect from './LiftSelect'
import styled from 'styled-components'
import { LoadingButton } from '@mui/lab'
import * as Yup from 'yup'
import AssetPhotoDropzone from './AssetPhotoDropzone'
import { isRequired, mustBeString } from '../utils/validation'

const _HiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1
})

interface RuuviTagFormProps {
  project?: Project
  values: RuuviTagInput
  onSubmit?: (values: RuuviTagInput) => Promise<void>
  onCancel?: () => void
  onChange?: (values: RuuviTagInput) => void
  cancelButtonLabel?: string
}

export function RuuviTagForm({
  project,
  values,
  onSubmit,
  onCancel,
  onChange,
  cancelButtonLabel
}: RuuviTagFormProps) {
  /**
   * The translate function.
   */
  const [t] = useTranslation('common')

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The uploaded file.
   */
  const [file, setFile] = useState<File | null>(null)

  /**
   * Indicates whether the photo dialog is open.
   */
  const [_isPhotoDialogOpen, setIsPhotoDialogOpen] = useState<boolean>(false)

  /**
   * The URL of the uploaded file.
   */
  const _fileUrl = file !== null ? URL.createObjectURL(file) : null

  /**
   * The initial values.
   */
  const initialValues: RuuviTagInput = {
    id: values?.id ?? '',
    device: values?.device ?? null,
    asset: {
      name: values?.asset?.name ?? '',
      description: values?.asset?.description,
      photo: values?.asset?.photo ?? null,
      project: values?.asset?.project ?? null,
      company: values?.asset?.company ?? null,
      level: values?.asset?.level ?? null,
      lift: values?.asset?.lift ?? null
    }
  }

  /**
   * Open the photo dialog.
   */
  const openPhotoDialog = () => {
    setIsPhotoDialogOpen(true)
  }

  /**
   * Close the photo dialog.
   */
  const closePhotoDialog = () => {
    setIsPhotoDialogOpen(false)
  }

  /**
   * The validation schema.
   */
  const validationSchema = Yup.object().shape({
    asset: Yup.object().shape({
      name: Yup.string()
        .typeError(mustBeString(t('name')))
        .required(isRequired(t('name')))
    })
  })

  /**
   * Handle the photo upload.
   */
  const _handlePhotoUpload = (event: ChangeEvent) => {
    const target = event.target as HTMLInputElement
    const newFile = target.files?.[0]

    if (newFile && newFile.type.startsWith('image/')) {
      setFile(newFile)
      openPhotoDialog()
    }
  }

  /**
   * Handle the photo delete.
   */
  const _handlePhotoDelete = () => {
    setFile(null)
    closePhotoDialog()
  }

  const lastEmittedValuesRef = useRef<string>('')

  /**
   * Handle the cancel event.
   */
  const handleCancel = () => {
    if (onCancel) {
      onCancel()
    }
  }

  /**
   * Handle the submit even.
   */
  const handleSubmit = async (
    values: RuuviTagInput,
    { setSubmitting }: FormikHelpers<RuuviTagInput>
  ) => {
    try {
      setSubmitting(true)

      if (values?.asset) {
        values.asset.photo = file
      }

      if (onSubmit) {
        await onSubmit(values)
      }
    } catch (_error: unknown) {
      // TODO: Handle error.
    } finally {
      setSubmitting(false)
    }
  }

  useEffect(() => {
    const controller = new AbortController()

    dispatch(getProjects())
    dispatch(getCompanies())

    if (project?.id) {
      dispatch(getLevelsByProjectId(project.id, { signal: controller.signal }))
      dispatch(getLiftsByProjectId(project.id, { signal: controller.signal }))
    }

    return () => {
      controller.abort()
    }
  }, [dispatch, project?.id])

  const formik = useFormik<RuuviTagInput>({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit
  })

  // Emit onChange whenever values change
  useEffect(() => {
    if (!onChange) return
    const serializableValues = {
      ...formik.values,
      asset: formik.values?.asset
        ? {
            ...formik.values.asset,
            photo: undefined
          }
        : undefined
    }
    const serializedValues = JSON.stringify(serializableValues)
    if (serializedValues !== lastEmittedValuesRef.current) {
      lastEmittedValuesRef.current = serializedValues
      onChange(formik.values)
    }
  }, [formik.values])

  return (
    <form onSubmit={formik.handleSubmit}>
      <Grid container spacing={6}>
        {/*<Grid item xs={12} lg={6}>
              <TextField
                name="id"
                label={t('macAddress')}
                value={values?.id}
                fullWidth
                disabled
              />
            </Grid>

            <Grid item xs={12} lg={6}>
              <TextField
                name="device"
                label={t('device')}
                value={values?.device}
                fullWidth
                disabled
              />
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>
            */}

        {/*<Grid item xs={12}>
              <Stack
                direction="row"
                gap={3}
                justifyContent="space-between"
                alignItems="center"
              >
                {fileUrl ? (
                  <>
                    <Paper elevation={6} onClick={openPhotoDialog}>
                      <img
                        src={fileUrl}
                        style={{
                          display: 'block',
                          objectFit: 'cover',
                          width: '64px',
                          height: '64px',
                          borderRadius: '4px'
                        }}
                      />
                    </Paper>

                    <Stack direction="row" gap={3}>
                      <IconButton color="error" size="small" onClick={handlePhotoDelete}>
                        <Delete />
                      </IconButton>
                    </Stack>
                  </>
                ) : (
                  <Button component="label" variant="contained" tabIndex={-1}>
                    {t('uploadNewPhoto')}
                    <HiddenInput type="file" onChange={handlePhotoUpload} />
                  </Button>
                )}
              </Stack>
              <Dialog open={isPhotoDialogOpen} onClose={closePhotoDialog} fullWidth maxWidth="sm">
                <DialogTitle>{t('previewPhoto')}</DialogTitle>
                <DialogContent>
                  {fileUrl !== null && (
                    <Paper elevation={6}>
                      <img
                        src={fileUrl}
                        style={{
                          display: 'block',
                          objectFit: 'cover',
                          width: '552px',
                          height: '552px',
                          borderRadius: '4px'
                        }}
                      />
                    </Paper>
                  )}
                </DialogContent>
                <DialogActions>
                  <Button onClick={closePhotoDialog}>{t('close')}</Button>
                  <Button color="error" onClick={handlePhotoDelete}>
                    {t('delete')}
                  </Button>
                </DialogActions>
              </Dialog>
            </Grid>*/}

        <Grid item xs={12}>
          <TextField
            name="asset.name"
            label={t('name')}
            value={formik.values?.asset?.name ?? ''}
            // @ts-expect-error typed incorrectly
            error={Boolean(formik.touched?.asset?.name && formik.errors?.asset?.name)}
            // @ts-expect-error typed incorrectly
            helperText={formik.touched?.asset?.name && formik.errors?.asset?.name}
            fullWidth
            disabled={formik.isSubmitting}
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
          />
        </Grid>

        {/*<Grid item xs={12}>
              <TextField
                multiline
                name="asset.description"
                label={t('description')}
                value={values?.asset?.description ?? ''}
                fullWidth
                onChange={handleChange}
              />
            </Grid>*/}

        {/*<Grid item xs={12}>
              <CompanySelect
                name="asset.company"
                label={t('company')}
                value={values?.asset?.company ?? ''}
                fullWidth
                disabled
                onChange={handleChange}
              />
            </Grid>*/}

        {/*<Grid item xs={12}>
              <ProjectSelect
                name="asset.project"
                label={t('project')}
                value={values?.asset?.project ?? ''}
                fullWidth
                disabled
                onChange={handleChange}
              />
            </Grid>*/}

        <Grid item xs={12}>
          <LevelSelect
            name="asset.level"
            label={t('level')}
            value={formik.values?.asset?.level ?? null}
            disabled={formik.isSubmitting}
            withAddOption={project !== undefined}
            withClearSelection={true}
            fullWidth
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
          />
        </Grid>

        <Grid item xs={12}>
          <LiftSelect
            name="asset.lift"
            label={t('lift')}
            value={formik.values?.asset?.lift ?? null}
            disabled={formik.isSubmitting}
            withAddOption={project !== undefined}
            withClearSelection={true}
            fullWidth
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
          />
        </Grid>

        <Grid item xs={12}>
          <AssetPhotoDropzone value={file} onChange={setFile} />
        </Grid>

        <Grid item xs={12}>
          <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={2}>
            <Button variant="text" color="inherit" onClick={handleCancel}>
              {cancelButtonLabel ?? t('cancel')}
            </Button>
            <LoadingButton
              loading={formik.isSubmitting}
              variant="contained"
              color="primary"
              type="submit"
              disabled={Object.keys(formik.errors).length > 0}
            >
              {t('save')}
            </LoadingButton>
          </Stack>
        </Grid>
      </Grid>
    </form>
  )
}
